import { useCallback, useEffect, useMemo, useState, type MutableRefObject } from "react";
import moment from "moment";
import { Loader } from "@themes/pollinator/ui/Loader";
import { StickyActionBar } from "@themes/pollinator/ui/StickyActionBar";
import { StepActions } from "@themes/pollinator/ui/StepActions";
import { Type } from "@themes/pollinator/ui/Type";
import { TimezoneOption, useTimezoneOptions } from "@core/features/timezones";
import { getValidTimeslotsFromContiguousTimeslots } from "@core/features/timeslots";
import {
  useScheduling,
  useSubmitSchedulingAvailability,
  useGetSchedulingAvailability,
  generateWeekDates,
  HeaderFormat,
  START_HOUR,
  END_HOUR,
  MIN_CONTIGUOUS_TIMESLOT_DURATION,
  MIN_REQUIRED_HOURS,
  ProcessedSelectableOption,
} from "@core/features/scheduling";
import type { SchedulingMode, SelectableOption } from "@core/features/actions";
import { usePlatformStore } from "@platform/store";
import {
  useStepFormSync,
  type StepValidationState,
} from "@platform/features/onboarding";
import { Scheduling } from "@platform/features/programScheduling";
import { SchedulingRepeatingDropdown } from "../../../programScheduling/components/SchedulingRepeatingDropdown";

export type Props = {
  /** The program ID */
  programId: number;
  /** Optional scheduled engagement start date (for freeform mode) */
  scheduledEngagementStartDate?: string;
  /** Callback when scheduling is successfully submitted */
  onSuccess: () => void;
  /** Optional callback when user skips scheduling */
  onSkip?: () => void;
  /** Optional callback when an error occurs */
  onError?: () => void;
  /** Display variant - "inline" shows StickyActionBar, "embedded" renders just the Scheduling component */
  variant?: "inline" | "embedded";
  /** For repeating-dropdown mode: use the dropdown's internal Edit/Cancel/Save buttons instead of external StepActions */
  useDropdownActions?: boolean;
  /** Custom primary button text */
  primaryButtonText?: string;
  /** Additional CSS classes */
  className?: string;
  /** Selection mode */
  mode?: SchedulingMode;
  /** Grid start hour */
  startHour?: number;
  /** Grid end hour */
  endHour?: number;
  /** Minimum required hours for freeform mode */
  minRequiredHours?: number;
  /** Minimum contiguous duration for freeform mode */
  minContiguousDuration?: number;
  /** Selectable options for select-options and repeating modes. utcDates is derived from these. */
  selectableOptions?: SelectableOption[];
  /** Number of options user must select for select-options mode */
  requiredSelections?: number;
  /** Maximum number of selections allowed (for options modes) */
  maxSelections?: number;
  /** Header format: 'date' shows "Jan 14 PST", 'weekday' shows "Mon" */
  headerFormat?: HeaderFormat;
  /** Optional deadline text */
  deadline?: string;
  /** Optional heading to display above the scheduling content */
  heading?: string;
  /** Optional description to display below the heading */
  description?: string;
  /** Accessible label for the repeating-dropdown radio group */
  label?: string;
  /** Callback when step validation state changes (for parent footer) */
  onStepStateChange?: (state: StepValidationState) => void;
  /** Ref to expose the submit handler to parent */
  submitRef?: MutableRefObject<(() => Promise<void> | void) | null>;
  /** When true, disables all interaction and hides action bars */
  isReadOnly?: boolean;
};

/**
 * Container component for scheduling availability.
 * Handles timezone options, fetching saved slots, slot selection, and submission.
 *
 * Used by both SchedulingDialog and StepContent for onboarding.
 */
export function SchedulingContainer({
  programId,
  scheduledEngagementStartDate,
  onSuccess,
  onSkip,
  onError,
  variant = "inline",
  useDropdownActions = false,
  primaryButtonText = "Save",
  className,
  mode = "freeform",
  startHour = START_HOUR,
  endHour = END_HOUR,
  minRequiredHours = MIN_REQUIRED_HOURS,
  minContiguousDuration = MIN_CONTIGUOUS_TIMESLOT_DURATION,
  selectableOptions = [],
  requiredSelections = 0,
  maxSelections,
  headerFormat = "weekday",
  deadline,
  heading,
  description,
  label,
  onStepStateChange,
  submitRef,
  isReadOnly = false,
}: Props) {
  const user = usePlatformStore((s) => s.user);

  // Process selectableOptions: generate IDs and compute day from date
  const { processedOptions, derivedUtcDates } = useMemo(() => {
    if (!selectableOptions.length) {
      return {
        processedOptions: [] as ProcessedSelectableOption[],
        derivedUtcDates: [] as string[],
      };
    }

    // Generate IDs for options that don't have them, compute day from date
    const processedOptions: ProcessedSelectableOption[] = selectableOptions.map(
      (opt, index) => ({
        ...opt,
        id:
          opt.id ||
          `opt-${opt.date}-${opt.startHour}-${opt.startMinute}-${index}`,
        day: opt.day ?? moment.utc(opt.date).day(),
      })
    );

    // Derive unique dates from options
    const derivedUtcDates = [
      ...new Set(processedOptions.map((opt) => opt.date)),
    ].sort();

    return { processedOptions, derivedUtcDates };
  }, [selectableOptions]);

  // Use derived dates from options, or generate 7-day week for freeform mode
  const utcDates = useMemo(() => {
    if (derivedUtcDates.length) return derivedUtcDates;
    return generateWeekDates(scheduledEngagementStartDate);
  }, [derivedUtcDates, scheduledEngagementStartDate]);

  // Timezone handling
  const { timezones, selectedTimezone, handleTimezoneChange } =
    useTimezoneOptions({ userTimezone: user?.timezone });

  // Fetch saved time slots
  const {
    data: savedTimeSlots,
    isLoading,
    isError,
    refetch,
  } = useGetSchedulingAvailability(programId);

  // Scheduling slot selection and grouping logic
  const {
    contiguousTimeslots,
    timeSlots,
    selectedTimeSlots,
    handleMouseDown,
    handleMouseOver,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    validHoursCount,
    requirementsMet,
    headers,
    columnCount,
    selectedOptionIds,
    selectableOptions: visibleSelectableOptions,
    toggleOptionSelection,
  } = useScheduling({
    utcDates,
    savedTimeSlots: savedTimeSlots || [],
    selectedTimezone: selectedTimezone || ({} as TimezoneOption),
    mode,
    startHour,
    endHour,
    minRequiredHours,
    minContiguousDuration,
    selectableOptions: processedOptions,
    requiredSelections,
    maxSelections,
    headerFormat,
  });

  // Edit mode state for repeating-dropdown mode
  // Start in editing mode only if there's no saved data
  const hasSavedData = savedTimeSlots && savedTimeSlots.length > 0;
  const [isEditing, setIsEditing] = useState(!hasSavedData);
  const [savedSelectionIds, setSavedSelectionIds] = useState<string[]>([]);
  const [hasInitialized, setHasInitialized] = useState(false);

  // Initialize saved selection from server data (for repeating-dropdown mode)
  useEffect(() => {
    if (mode === "repeating-dropdown" && !hasInitialized && !isLoading) {
      setHasInitialized(true);
      if (savedTimeSlots && savedTimeSlots.length > 0) {
        // Has saved data from server - show completed mode
        setSavedSelectionIds(selectedOptionIds);
        setIsEditing(false);
      } else {
        // No saved data - stay in editing mode
        setIsEditing(true);
      }
    }
  }, [mode, savedTimeSlots, selectedOptionIds, isLoading, hasInitialized]);

  // Mutation to submit scheduling availability
  const submitSchedulingAvailability = useSubmitSchedulingAvailability({
    programId,
    onSuccess: () => {
      refetch();
      onSuccess();
    },
  });

  const { isPending, isError: isSubmitError } = submitSchedulingAvailability;

  // Handle error state
  useEffect(() => {
    if (isError || isSubmitError) {
      onError?.();
    }
  }, [isError, isSubmitError, onError]);

  // Edit mode handlers for repeating-dropdown mode
  const handleEdit = useCallback(() => {
    setIsEditing(true);
  }, []);

  const handleCancelEdit = useCallback(() => {
    // Revert to saved selection by finding unique time ranges to toggle
    // toggleOptionSelection handles all matching options for a time range,
    // so we only need to toggle once per unique time range

    // Helper to get time range key from option
    const getTimeRangeKey = (id: string) => {
      const opt = visibleSelectableOptions.find((o) => o.id === id);
      if (!opt) return null;
      return `${opt.startHour}:${opt.startMinute}-${opt.endHour}:${opt.endMinute}`;
    };

    // Get unique time ranges for current and saved selections
    const currentTimeRanges = new Map<string, string>(); // timeRangeKey -> optionId
    selectedOptionIds.forEach((id) => {
      const key = getTimeRangeKey(id);
      if (key && !currentTimeRanges.has(key)) {
        currentTimeRanges.set(key, id);
      }
    });

    const savedTimeRanges = new Map<string, string>(); // timeRangeKey -> optionId
    savedSelectionIds.forEach((id) => {
      const key = getTimeRangeKey(id);
      if (key && !savedTimeRanges.has(key)) {
        savedTimeRanges.set(key, id);
      }
    });

    // Find time ranges to remove (in current but not in saved)
    const toRemove: string[] = [];
    currentTimeRanges.forEach((optionId, timeRangeKey) => {
      if (!savedTimeRanges.has(timeRangeKey)) {
        toRemove.push(optionId);
      }
    });

    // Find time ranges to add (in saved but not in current)
    const toAdd: string[] = [];
    savedTimeRanges.forEach((optionId, timeRangeKey) => {
      if (!currentTimeRanges.has(timeRangeKey)) {
        toAdd.push(optionId);
      }
    });

    // Toggle once per unique time range (order matters: remove first, then add)
    toRemove.forEach((id) => toggleOptionSelection(id));
    toAdd.forEach((id) => toggleOptionSelection(id));

    setIsEditing(false);
  }, [selectedOptionIds, savedSelectionIds, visibleSelectableOptions, toggleOptionSelection]);

  const handleSaveEdit = useCallback(() => {
    // Check if selection has changed to avoid duplicate key error
    const currentSet = new Set(selectedOptionIds);
    const savedSet = new Set(savedSelectionIds);
    const hasChanged =
      currentSet.size !== savedSet.size ||
      [...currentSet].some((id) => !savedSet.has(id));

    if (!hasChanged) {
      // No changes, just exit editing mode
      setIsEditing(false);
      return;
    }

    // Deduplicate time slot IDs (same weekly slot may appear for multiple days)
    const validTimeSlotIds = [...new Set(selectedTimeSlots.map((s) => s.id))];
    submitSchedulingAvailability.mutateAsync(validTimeSlotIds).then(() => {
      setSavedSelectionIds(selectedOptionIds);
      setIsEditing(false);
    });
  }, [selectedTimeSlots, selectedOptionIds, savedSelectionIds, submitSchedulingAvailability]);

  const handleSubmit = useCallback(() => {
    const validTimeSlots =
      getValidTimeslotsFromContiguousTimeslots(contiguousTimeslots);
    submitSchedulingAvailability.mutateAsync(validTimeSlots);
  }, [contiguousTimeslots, submitSchedulingAvailability]);

  // Use appropriate submit handler based on mode
  const submitHandler = mode === "repeating-dropdown" ? handleSaveEdit : handleSubmit;

  // Sync form state with parent when variant is "embedded"
  useStepFormSync({
    shouldSync: variant === "embedded",
    onStepStateChange,
    submitRef,
    formState: {
      isValid: requirementsMet,
      isDirty: true, // Scheduling is always considered "dirty" once user interacts
      isSubmitting: isPending,
    },
    onSubmit: submitHandler,
    canProceed: requirementsMet && !isPending,
  });

  if (isLoading) {
    return <Loader isFullPage />;
  }

  // Render the appropriate scheduling UI based on mode
  const renderSchedulingContent = (hideDropdownActions = false) => {
    if (mode === "repeating-dropdown") {
      // When read-only, force completed mode and hide all actions
      // When actions are hidden (parent handles them), always show editing mode
      // so users can interact with options
      const dropdownMode = isReadOnly
        ? "completed"
        : hideDropdownActions
          ? "editing"
          : (isEditing ? "editing" : "completed");
      return (
        <SchedulingRepeatingDropdown
          isSubmitting={isPending}
          timezone={selectedTimezone || ({} as TimezoneOption)}
          selectableOptions={visibleSelectableOptions}
          selectedOptionIds={selectedOptionIds}
          onToggleOption={toggleOptionSelection}
          requiredSelections={requiredSelections}
          maxSelections={maxSelections}
          mode={dropdownMode}
          onEdit={handleEdit}
          onCancel={savedSelectionIds.length > 0 ? handleCancelEdit : undefined}
          onSave={handleSaveEdit}
          isSaveDisabled={!requirementsMet || isPending}
          hideActions={hideDropdownActions || isReadOnly}
          label={label}
        />
      );
    }

    return (
      <Scheduling
        timeZoneOptions={timezones}
        selectedTimezone={selectedTimezone || ({} as TimezoneOption)}
        onTimezoneChange={handleTimezoneChange}
        isSubmitting={isPending}
        isLoading={isLoading}
        timeSlots={timeSlots}
        selectedTimeSlots={selectedTimeSlots}
        contiguousTimeslots={contiguousTimeslots}
        handleMouseDown={handleMouseDown}
        handleMouseOver={handleMouseOver}
        handleTouchStart={handleTouchStart}
        handleTouchMove={handleTouchMove}
        handleTouchEnd={handleTouchEnd}
        validHoursCount={validHoursCount}
        mode={mode}
        headers={headers}
        columnCount={columnCount}
        selectableOptions={visibleSelectableOptions}
        selectedOptionIds={selectedOptionIds}
        minRequiredHours={minRequiredHours}
        minContiguousDuration={minContiguousDuration}
        requiredSelections={requiredSelections}
        deadline={deadline}
        isReadOnly={isReadOnly}
      />
    );
  };

  // For embedded variant (used in dialogs), render just the Scheduling component
  // The parent component handles action buttons
  if (variant === "embedded") {
    // Hide dropdown's internal actions in embedded mode - parent handles actions
    const hideDropdownActionsInEmbedded = mode === "repeating-dropdown";
    return (
      <>
        {isPending && <Loader isAbsolute />}
        {renderSchedulingContent(hideDropdownActionsInEmbedded)}
      </>
    );
  }

  // Inline variant with action bar (used in onboarding)
  // For repeating-dropdown mode, use StepActions unless useDropdownActions is true
  const isRepeatingDropdown = mode === "repeating-dropdown";
  // Hide dropdown's internal actions when we're using external actions (StepActions)
  const hideDropdownActions = isRepeatingDropdown && !useDropdownActions;

  return (
    <>
      <div className={className || "w-full flex flex-col gap-6 p-6 flex-1 justify-center"}>
        {isPending && <Loader isAbsolute />}
        {(heading || description) && (
          <div className="flex flex-col gap-2 max-w-3xl mx-auto w-full">
            {heading && (
              <Type variant="h2" as="h2">
                {heading}
              </Type>
            )}
            {description && (
              <Type variant="body1" className="text-brand-600">
                {description}
              </Type>
            )}
          </div>
        )}
        <div className="max-w-3xl mx-auto w-full">
          {renderSchedulingContent(hideDropdownActions)}
        </div>
      </div>
      {!isReadOnly && (
        isRepeatingDropdown && !useDropdownActions ? (
          <StepActions
            stepIndex={0}
            totalSteps={1}
            primaryLabel={primaryButtonText}
            onPrimaryClick={handleSaveEdit}
            isPrimaryDisabled={!requirementsMet || isPending}
            onSkip={onSkip}
            showProgress={false}
          />
        ) : !isRepeatingDropdown ? (
          <StickyActionBar
            primaryAction={{
              label: primaryButtonText,
              onClick: handleSubmit,
              disabled: !requirementsMet || isPending,
              isLoading: isPending,
            }}
            secondaryAction={
              onSkip
                ? {
                    label: "Skip for now",
                    onClick: onSkip,
                    disabled: isPending,
                  }
                : undefined
            }
          />
        ) : null
      )}
    </>
  );
}

export default SchedulingContainer;
